import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, DatabaseIcon, CodeIcon, SparklesIcon, MapPinIcon, DownloadIcon, CheckCircle2Icon, TerminalIcon, MailIcon, LinkedinIcon, GlobeIcon, ArrowRightIcon, BarChart3Icon, PieChartIcon, TargetIcon } from 'lucide-react';
export function ProductSuite() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3364">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-3365">
        {/* Ambient floating blob */}
        <motion.div animate={{
        x: [0, 40, -30, 0],
        y: [0, -25, 20, 0],
        scale: [1, 1.15, 0.9, 1]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-[-15%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-100 blur-[100px] opacity-40 pointer-events-none" data-id="element-3366" />
        <div className="text-center max-w-3xl mx-auto mb-16 relative z-10" data-id="element-3367">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-3368">
            Complete local business{' '}
            <motion.span animate={{
            opacity: [1, 0.7, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="text-blue-600" data-id="element-3369">
              intelligence suite
            </motion.span>
          </h2>
          <p className="text-lg text-slate-500" data-id="element-3370">
            Everything you need to find, enrich, and connect with local
            businesses worldwide
          </p>
        </div>

        {/* Bento Grid — 5:3:3:5 ratio */}
        <div className="grid grid-cols-1 md:grid-cols-8 gap-6" data-id="element-3371">
          {/* 1. Local businesses extractor (col-span-5) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="md:col-span-5 rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-600/10 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3372">
            <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3373"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col lg:flex-row gap-8 items-center relative z-10" data-id="element-3374">
              <div className="flex-1 space-y-6" data-id="element-3375">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors border border-slate-100" data-id="element-3376">
                  <motion.div whileHover={{
                  rotate: 15,
                  scale: 1.1
                }} transition={{
                  duration: 0.2
                }} data-id="element-3377">
                    <SearchIcon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-3378" />
                  </motion.div>
                </div>
                <div data-id="element-3379">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-3380">
                    Local businesses extractor
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6" data-id="element-3381">
                    Scrape business information from Google Maps into CSV/XLSX
                    files. Extract verified contacts, ratings, and business
                    metadata in minutes.
                  </p>
                  <ul className="space-y-3 mb-8" data-id="element-3382">
                    {['Real-time extraction', 'CSV/Excel export', 'Unlimited searches'].map((bullet, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-id="element-3383">
                        <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3384">
                          <CheckCircle2Icon className="w-3 h-3" data-id="element-3385" />
                        </div>
                        {bullet}
                      </li>)}
                  </ul>
                  <a href="#/google-maps-scraper" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm group-hover:shadow" data-id="element-3386">
                    Start extracting <ArrowRightIcon className="w-4 h-4" data-id="element-3387" />
                  </a>
                </div>
              </div>

              {/* UI Mockup */}
              <div className="w-full lg:w-[320px] shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 relative overflow-hidden group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3388">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100" data-id="element-3389">
                  <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-2 border border-slate-100" data-id="element-3390">
                    <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-3391" />
                    <span className="text-xs text-slate-600 font-medium" data-id="element-3392">
                      Restaurants in Austin, TX
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3393">
                    <DownloadIcon className="w-4 h-4" data-id="element-3394" />
                  </div>
                </div>
                <div className="space-y-3 relative" data-id="element-3395">
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent z-10" data-id="element-3396" />
                  {[1, 2, 3].map(i => <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-3397">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0" data-id="element-3398">
                        <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-3399" />
                      </div>
                      <div data-id="element-3400">
                        <div className="text-sm font-bold text-slate-900" data-id="element-3401">
                          Austin Taco Co. {i}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-3402">
                          4.8 ★ (1.2k reviews) • Mexican
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>

          {/* 2. Local business database (col-span-3, light blue bg) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="md:col-span-3 rounded-3xl border border-blue-100 bg-blue-50/40 overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-600/10 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3403">
            <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3404"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col relative z-10" data-id="element-3405">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-50 transition-colors border border-blue-100" data-id="element-3406">
                <motion.div whileHover={{
                rotate: 15,
                scale: 1.1
              }} transition={{
                duration: 0.2
              }} data-id="element-3407">
                  <DatabaseIcon className="w-6 h-6 text-blue-600 group-hover:text-blue-700 transition-colors" data-id="element-3408" />
                </motion.div>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-3409">
                Local business database
              </h3>
              <p className="text-slate-500 leading-relaxed mb-8" data-id="element-3410">
                Download ready-to-use business datasets by category and
                location. Pre-built datasets updated weekly.
              </p>

              {/* UI Mockup */}
              <div className="mt-auto bg-white rounded-2xl border border-blue-100 shadow-sm p-4 space-y-3 mb-8 group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3411">
                {[{
                name: 'TechFlow Solutions',
                type: 'Software',
                verified: true
              }, {
                name: 'Apex Dental',
                type: 'Healthcare',
                verified: true
              }, {
                name: 'Summit Roofing',
                type: 'Contractor',
                verified: false
              }].map((row, i) => <div key={i} className="bg-white rounded-xl p-3 border border-blue-100/50 shadow-sm flex items-center justify-between" data-id="element-3412">
                    <div className="flex items-center gap-2" data-id="element-3413">
                      <div className="w-6 h-6 rounded bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold" data-id="element-3414">
                        {row.name[0]}
                      </div>
                      <div data-id="element-3415">
                        <div className="text-xs font-bold text-slate-900" data-id="element-3416">
                          {row.name}
                        </div>
                        <div className="text-[9px] text-slate-400" data-id="element-3417">
                          {row.type}
                        </div>
                      </div>
                    </div>
                    {row.verified && <CheckCircle2Icon className="w-3.5 h-3.5 text-green-500" data-id="element-3418" />}
                  </div>)}
              </div>

              <div className="mt-auto" data-id="element-3419">
                <ul className="space-y-2 mb-6" data-id="element-3420">
                  {['200M+ records', '97-99% accuracy', 'Monthly updates'].map((bullet, i) => <li key={i} className="flex items-center gap-2 text-xs font-medium text-slate-600" data-id="element-3421">
                        <span className="w-1 h-1 rounded-full bg-blue-600" data-id="element-3422" />
                        {bullet}
                      </li>)}
                </ul>
                <a href="#/local-business-database" className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm" data-id="element-3423">
                  Explore database
                </a>
              </div>
            </div>
          </motion.div>

          {/* 3. Local business API (col-span-3, Dark) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="md:col-span-3 rounded-3xl border border-slate-800 bg-slate-900 overflow-hidden flex flex-col group hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3424">
            <div className="absolute inset-0 bg-blue-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3425"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col relative z-10" data-id="element-3426">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-900/50 transition-colors border border-slate-700" data-id="element-3427">
                <motion.div whileHover={{
                rotate: 15,
                scale: 1.1
              }} transition={{
                duration: 0.2
              }} data-id="element-3428">
                  <CodeIcon className="w-6 h-6 text-slate-300 group-hover:text-blue-400 transition-colors" data-id="element-3429" />
                </motion.div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors" data-id="element-3430">
                Local business API
              </h3>
              <p className="text-slate-400 leading-relaxed mb-8" data-id="element-3431">
                Integrate our 200M+ business database directly into your product
                or internal tools.
              </p>

              {/* UI Mockup */}
              <div className="mt-auto group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3432">
                <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden" data-id="element-3433">
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-800 bg-slate-900/50" data-id="element-3434">
                    <div className="w-2 h-2 rounded-full bg-red-500/80" data-id="element-3435" />
                    <div className="w-2 h-2 rounded-full bg-yellow-500/80" data-id="element-3436" />
                    <div className="w-2 h-2 rounded-full bg-green-500/80" data-id="element-3437" />
                    <span className="text-[9px] text-slate-500 font-mono ml-2 flex items-center gap-1" data-id="element-3438">
                      <TerminalIcon className="w-3 h-3" data-id="element-3439" /> api.openmart.com
                    </span>
                  </div>
                  <div className="p-4 font-mono text-[11px] leading-relaxed" data-id="element-3440">
                    <div className="text-blue-400" data-id="element-3441">
                      GET{' '}
                      <span className="text-slate-300" data-id="element-3442">
                        /v1/businesses/search
                      </span>
                    </div>
                    <div className="text-slate-500 mt-2" data-id="element-3443">{'{'}</div>
                    <div className="pl-4 text-slate-300" data-id="element-3444">
                      <span className="text-sky-300" data-id="element-3445">"status"</span>:{' '}
                      <span className="text-green-400" data-id="element-3446">200</span>,<br data-id="element-3447" />
                      <span className="text-sky-300" data-id="element-3448">"data"</span>: {'[{'}
                      <br data-id="element-3449" />
                      <div className="pl-4" data-id="element-3450">
                        <span className="text-sky-300" data-id="element-3451">"name"</span>:{' '}
                        <span className="text-amber-300" data-id="element-3452">"Austin Taco"</span>,
                        <br data-id="element-3453" />
                        <span className="text-sky-300" data-id="element-3454">"emails"</span>:{' '}
                        <span className="text-amber-300" data-id="element-3455">["info@..."]</span>
                      </div>
                      {'}]'}
                    </div>
                    <div className="text-slate-500" data-id="element-3456">{'}'}</div>
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-6" data-id="element-3457">
                <ul className="space-y-2 mb-6" data-id="element-3458">
                  {['RESTful API', 'Real-time data', '99.9% uptime'].map((bullet, i) => <li key={i} className="flex items-center gap-2 text-xs font-medium text-slate-400" data-id="element-3459">
                        <span className="w-1 h-1 rounded-full bg-blue-500" data-id="element-3460" />
                        {bullet}
                      </li>)}
                </ul>
                <a href="#/local-business-api" className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-full font-bold text-sm text-white hover:bg-slate-700 hover:border-slate-600 transition-colors shadow-sm" data-id="element-3461">
                  View API docs
                </a>
              </div>
            </div>
          </motion.div>

          {/* 4. Local business enrichment (col-span-5) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="md:col-span-5 rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-600/10 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3462">
            <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3463"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col lg:flex-row-reverse gap-8 items-center relative z-10" data-id="element-3464">
              <div className="flex-1 space-y-6" data-id="element-3465">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors border border-slate-100" data-id="element-3466">
                  <motion.div whileHover={{
                  rotate: 15,
                  scale: 1.1
                }} transition={{
                  duration: 0.2
                }} data-id="element-3467">
                    <SparklesIcon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-3468" />
                  </motion.div>
                </div>
                <div data-id="element-3469">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-3470">
                    Local business enrichment
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6" data-id="element-3471">
                    Append missing data points to your existing business lists.
                    Find verified owner emails, direct phone numbers, and social
                    profiles instantly.
                  </p>
                  <ul className="space-y-3 mb-8" data-id="element-3472">
                    {['Verified owner emails', 'Direct phone numbers', 'Social media profiles'].map((bullet, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-id="element-3473">
                        <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3474">
                          <CheckCircle2Icon className="w-3 h-3" data-id="element-3475" />
                        </div>
                        {bullet}
                      </li>)}
                  </ul>
                  <a href="#/local-business-enrichment" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm group-hover:shadow" data-id="element-3476">
                    Start enriching <ArrowRightIcon className="w-4 h-4" data-id="element-3477" />
                  </a>
                </div>
              </div>

              {/* UI Mockup */}
              <div className="w-full lg:w-[340px] shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 relative overflow-hidden group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3478">
                <div className="flex items-center justify-between mb-4" data-id="element-3479">
                  <div className="flex items-center gap-3" data-id="element-3480">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold" data-id="element-3481">
                      SJ
                    </div>
                    <div data-id="element-3482">
                      <div className="text-sm font-bold text-slate-900" data-id="element-3483">
                        Sarah Jenkins
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-3484">
                        TechFlow Solutions
                      </div>
                    </div>
                  </div>
                  <div className="px-2.5 py-1 bg-purple-50 text-purple-600 rounded-md text-[10px] font-bold flex items-center gap-1" data-id="element-3485">
                    <SparklesIcon className="w-3 h-3" data-id="element-3486" /> Enriched
                  </div>
                </div>

                <div className="space-y-2.5" data-id="element-3487">
                  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3488">
                    <MailIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3489" />
                    <div className="flex-1 text-sm text-slate-700 font-medium" data-id="element-3490">
                      sarah@techflow.io
                    </div>
                    <CheckCircle2Icon className="w-4 h-4 text-green-500 shrink-0" data-id="element-3491" />
                  </div>
                  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3492">
                    <LinkedinIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3493" />
                    <div className="flex-1 text-sm text-blue-600 font-medium truncate" data-id="element-3494">
                      linkedin.com/in/sarahj
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3495">
                    <GlobeIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3496" />
                    <div className="flex-1 text-sm text-slate-700 font-medium" data-id="element-3497">
                      techflow.io
                    </div>
                  </div>
                </div>

                <motion.div animate={{
                y: [0, 200, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute top-0 left-0 right-0 h-0.5 bg-blue-400 opacity-30" data-id="element-3498" />
              </div>
            </div>
          </motion.div>

          {/* 5. Openclaw for sales (full-width) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="md:col-span-8 rounded-3xl p-[2px] overflow-hidden flex flex-col group hover:shadow-2xl hover:shadow-blue-600/15 hover:-translate-y-2 transition-all duration-300 relative" style={{
          background: 'linear-gradient(135deg, #156ef6, #4f94fe, #06b6d4, #156ef6)',
          backgroundSize: '300% 300%'
        }} data-id="element-3499">
            <motion.div animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute inset-0 rounded-3xl" style={{
            background: 'linear-gradient(135deg, #156ef6, #4f94fe, #06b6d4, #156ef6)',
            backgroundSize: '300% 300%'
          }} data-id="element-3500" />
            <div className="relative bg-white rounded-[calc(1.5rem-2px)] flex-1 flex flex-col overflow-hidden z-10" data-id="element-3501">
              <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3502"></div>
              <div className="p-8 md:p-10 flex-1 flex flex-col lg:flex-row gap-8 items-center relative z-10" data-id="element-3503">
                {/* Left: Text */}
                <div className="flex-1 space-y-6" data-id="element-3504">
                  <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors border border-slate-100" data-id="element-3505">
                    <motion.div whileHover={{
                    rotate: 15,
                    scale: 1.1
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-3506">
                      <BarChart3Icon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-3507" />
                    </motion.div>
                  </div>
                  <div data-id="element-3508">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3509">
                      <h3 className="text-2xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-3510">
                        Openclaw for sales
                      </h3>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white rounded-md text-[10px] font-bold" data-id="element-3511">
                        New
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md text-[10px] font-bold border border-blue-100" data-id="element-3512">
                        Enterprise
                      </span>
                    </div>
                    <p className="text-slate-500 leading-relaxed mb-6" data-id="element-3513">
                      AI-powered local business research and sales intelligence.
                      Find and score leads across 4 dimensions with weighted
                      priorities, and get personalized outreach emails drafted
                      automatically.
                    </p>
                    <ul className="space-y-3 mb-8" data-id="element-3514">
                      {['Lead scoring across 4 weighted dimensions', 'Personalized outreach emails for every lead', 'Decision-maker contacts with verified emails'].map((bullet, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-id="element-3515">
                          <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3516">
                            <CheckCircle2Icon className="w-3 h-3" data-id="element-3517" />
                          </div>
                          {bullet}
                        </li>)}
                    </ul>
                    <a href="#/openclaw" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm group-hover:shadow" data-id="element-3518">
                      Try Openclaw for sales{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-3519" />
                    </a>
                  </div>
                </div>

                {/* Right: Analytics Illustration */}
                <div className="w-full lg:w-[480px] shrink-0 group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3520">
                  <div className="bg-slate-50 rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col h-[420px]" data-id="element-3521">
                    {/* Chat Header */}
                    <div className="px-4 py-3 border-b border-stone-200/50 flex items-center justify-between bg-white/50" data-id="element-3522">
                      <span className="text-sm font-semibold text-stone-800" data-id="element-3523">
                        Sales lead list
                      </span>
                    </div>

                    {/* Chat Messages */}
                    <div className="p-4 space-y-4 flex-1 overflow-y-auto" data-id="element-3524">
                      {/* User Message */}
                      <div className="flex justify-end" data-id="element-3525">
                        <div className="bg-stone-800 text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm max-w-[85%] leading-relaxed shadow-sm" data-id="element-3526">
                          Find hair salons in San Francisco with 2+ locations
                        </div>
                      </div>

                      {/* Agent Response 1 */}
                      <div className="flex items-start gap-3" data-id="element-3527">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-3528">
                          <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-3529" />
                        </div>
                        <div className="flex-1 space-y-2" data-id="element-3530">
                          <p className="text-sm text-stone-800" data-id="element-3531">
                            Found <strong data-id="element-3532">28 qualified leads</strong>. Here are
                            the top results:
                          </p>
                          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden" data-id="element-3533">
                            <div className="divide-y divide-stone-100" data-id="element-3534">
                              {[{
                              name: 'Supercuts',
                              domain: 'supercuts.com',
                              size: '50–200',
                              score: 94
                            }, {
                              name: 'Sephora',
                              domain: 'sephora.com',
                              size: '200+',
                              score: 91
                            }, {
                              name: 'SmartStyle',
                              domain: 'smartstyle.com',
                              size: '50–200',
                              score: 87
                            }].map((lead, i) => <div key={i} className="flex items-center gap-3 px-3 py-2.5 hover:bg-stone-50/50" data-id="element-3535">
                                  <span className="text-xs font-semibold text-stone-700 w-[72px] shrink-0" data-id="element-3536">
                                    {lead.name}
                                  </span>
                                  <span className="text-[11px] text-stone-400 truncate flex-1" data-id="element-3537">
                                    {lead.domain}
                                  </span>
                                  <span className="text-[11px] text-stone-500 shrink-0" data-id="element-3538">
                                    {lead.size}
                                  </span>
                                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${lead.score >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`} data-id="element-3539">
                                    {lead.score}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Agent Response 2 */}
                      <div className="flex items-start gap-3" data-id="element-3540">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-3541">
                          <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-3542" />
                        </div>
                        <div className="flex-1 space-y-2" data-id="element-3543">
                          <p className="text-sm text-stone-800" data-id="element-3544">
                            I've drafted personalized emails for each lead:
                          </p>
                          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-3" data-id="element-3545">
                            <div className="space-y-1.5 mb-2" data-id="element-3546">
                              <div className="flex items-center gap-2 text-xs" data-id="element-3547">
                                <span className="font-semibold text-stone-400 w-10" data-id="element-3548">
                                  To:
                                </span>
                                <span className="text-stone-700" data-id="element-3549">
                                  sarah@salonluxesf.com
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs" data-id="element-3550">
                                <span className="font-semibold text-stone-400 w-10" data-id="element-3551">
                                  Subject:
                                </span>
                                <span className="text-stone-700 truncate" data-id="element-3552">
                                  Quick question about Salon Luxe SF's
                                  scheduling
                                </span>
                              </div>
                            </div>
                            <div className="border-t border-stone-100 pt-2 text-xs text-stone-600 line-clamp-2" data-id="element-3553">
                              Hi Sarah, I noticed Salon Luxe SF has grown to 3
                              locations across San Francisco—congrats on the
                              expansion!
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Chat Input */}
                    <div className="p-3 border-t border-stone-200/50 bg-white/50" data-id="element-3554">
                      <div className="relative" data-id="element-3555">
                        <input type="text" placeholder="Ask a follow-up question..." className="w-full pl-3 pr-10 py-2.5 rounded-xl border border-stone-200 bg-white text-xs focus:outline-none placeholder:text-stone-400" readOnly data-id="element-3556" />
                        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 bg-stone-100 rounded-lg flex items-center justify-center" data-id="element-3557">
                          <ArrowRightIcon className="w-3 h-3 text-stone-400" data-id="element-3558" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}